import {
	ArrowLeft as ArrowLeftIcon,
	ArrowRight as ArrowRightIcon,
	Download as DownloadIcon,
	Fullscreen as FullscreenIcon
} from "@mui/icons-material";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import moment from "moment";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Carousel } from "react-responsive-carousel";

import { ReactComponent as NoImageIcon } from "../../assets/icons/image-not-found-icon.svg";
import { useCreateImageURL, IDimensions, ICarouselOverlayDetails, ICoordinate } from "../../hooks/useCreateImageURL";
import { IVehicleLogRowDetails, IVehicleLogViolationDetails } from "../../screens/VehicleLogs";
import { IFullscreenImageProps } from "../../screens/VehicleLogs/VehicleLogContent";
import { IEvidenceImageURLDetails } from "../../types";
import { convertToTitleCase, downloadImage } from "../../utils/commonUtils";
import { BBOX_STROKE_COLOR, BBOX_STROKE_WIDTH, NON_HSRP_VIOLATION_NAMES } from "../../utils/constants";
import "./index.css";

interface IEvidenceImageProps {
	vehicleLogDetails: IVehicleLogRowDetails | null;
	bbox: [number, number, number, number][];
	violations: IVehicleLogViolationDetails[];
	activeImage: number;
	onOpenFullscreenImage?: (props: IFullscreenImageProps) => void;
	onChangeActiveImage: (value: number) => void;
}

interface ICarouselItemProps {
	src: string;
	frameID: string;
	bbox: [number, number, number, number][];
	imageDimensions: IDimensions;
	overlayDetails: ICarouselOverlayDetails;
	violations: IVehicleLogViolationDetails[];
	width: number;
	isFrame: boolean;
	handleShowFullscreenImage?: (src: string, overlayDetails: ICarouselOverlayDetails, isFrame: boolean) => void;
}

const CarouselThumb: FC<{ src: string }> = ({ src }) => {
	const [showError, setShowError] = useState<boolean>(false);

	if (showError) {
		return (
			<Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
				<NoImageIcon width={34} fill="#d5d5d5" stroke="#d5d5d5" />
			</Box>
		);
	}

	return <img src={src} alt={src} onError={() => setShowError(true)} />;
};

const CarouselItem: FC<ICarouselItemProps> = (props) => {
	const { src, frameID, bbox, imageDimensions, overlayDetails, violations, width, isFrame, handleShowFullscreenImage } =
		props;

	const canvasRef = useRef<HTMLCanvasElement>(null);

	const [canvasDimensions, setCanvasDimensions] = useState<IDimensions>({ width: 0, height: 0 });
	const [showError, setShowError] = useState<boolean>(false);
	const [downloadImageLoader, setDownloadImageLoader] = useState<boolean>(false);

	const { createImageURL, downloadCanvasRef } = useCreateImageURL();

	const canvasWrapperHeight = useMemo<number>(() => {
		if (canvasDimensions.height > 0) {
			return canvasDimensions.height;
		}

		return (width * imageDimensions.height) / imageDimensions.width;
	}, [canvasDimensions.height, imageDimensions, width]);

	function getScaledCoordinate(
		coordinate: ICoordinate,
		imageDimensions: IDimensions,
		canvasDimensions: IDimensions,
		getCanvasCoordinate = false
	): ICoordinate {
		const { height: imageHeight, width: imageWidth } = imageDimensions;
		const { height: canvasHeight, width: canvasWidth } = canvasDimensions;

		if (getCanvasCoordinate) {
			return {
				x: (canvasWidth * coordinate.x) / imageWidth,
				y: (canvasHeight * coordinate.y) / imageHeight
			};
		}

		return {
			x: (imageWidth * coordinate.x) / canvasWidth,
			y: (imageHeight * coordinate.y) / canvasHeight
		};
	}

	function handleDownload() {
		let fileName = `${frameID}.jpeg`;

		if (src.startsWith("http")) {
			const splitURL = src.split("/");
			const splitName = splitURL[splitURL.length - 1]?.split("?");
			fileName = splitName[0];
		}

		setDownloadImageLoader(true);

		createImageURL({
			src,
			overlayDetails,
			bbox,
			imageDimensions,
			isFrame,
			violations
		})
			.then((imageUrl) => {
				downloadImage(imageUrl, fileName).finally(() => {
					setDownloadImageLoader(false);
				});
			})
			.catch((error) => {
				console.log("Failed to download image:", error);
				setDownloadImageLoader(false);
			});
	}

	useEffect(() => {
		if (canvasRef.current && canvasRef.current.offsetWidth) {
			setCanvasDimensions((currentDimensions) => ({
				...currentDimensions,
				width: canvasRef.current?.offsetWidth ?? 0
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canvasRef.current, width]);

	useEffect(() => {
		if (canvasDimensions.width && imageDimensions) {
			const canvasHeight = (canvasDimensions.width * imageDimensions.height) / imageDimensions.width;

			setCanvasDimensions((currentDimensions) => ({
				...currentDimensions,
				height: canvasHeight
			}));
		}
	}, [canvasDimensions.width, imageDimensions]);

	useEffect(() => {
		if (canvasRef.current && src && isFrame && canvasDimensions.width && canvasDimensions.height) {
			const canvasContext = canvasRef.current.getContext("2d");
			if (!canvasContext) return;

			canvasContext.clearRect(0, 0, canvasDimensions.width, canvasDimensions.height);

			canvasContext.strokeStyle = BBOX_STROKE_COLOR;
			canvasContext.lineWidth = BBOX_STROKE_WIDTH;

			for (const bboxItem of bbox) {
				const startCoordinate: ICoordinate = getScaledCoordinate(
					{ x: bboxItem[0], y: bboxItem[1] },
					imageDimensions,
					canvasDimensions,
					true
				);
				const bboxDimensions: ICoordinate = getScaledCoordinate(
					{ x: bboxItem[2], y: bboxItem[3] },
					imageDimensions,
					canvasDimensions,
					true
				);

				canvasContext.strokeRect(startCoordinate.x, startCoordinate.y, bboxDimensions.x, bboxDimensions.y);
			}
		}
	}, [bbox, canvasDimensions, imageDimensions, isFrame, src, canvasRef]);

	useEffect(() => {
		if (src) {
			const srcImage = new Image();
			srcImage.src = src;
			srcImage.onerror = () => setShowError(true);
			srcImage.onload = () => setShowError(false);
		}
	}, [src]);

	if (showError) {
		return (
			<Box className="evidence-image-component-wrapper">
				<NoImageIcon width={58} fill="#d5d5d5" stroke="#d5d5d5" />
				<Typography variant="h6" color="#999999">
					Image not available
				</Typography>
			</Box>
		);
	}

	return (
		<Box className="evidence-image-wrapper" sx={{ width: width }}>
			<Box className="evidence-image-overlay top">
				<Box className="evidence-image-overlay-header-wrapper">
					<Box className="evidence-image-overlay-header">
						<h2>{overlayDetails.productName}</h2>
						<Box className="evidence-image-overlay-header-divider" />
						<h3>{overlayDetails.location}</h3>
					</Box>

					<Box className="evidence-image-overlay-timestamp-wrapper">
						<h4>{`Date: ${moment(overlayDetails.timestamp).format("YYYY-MM-DD")}`}</h4>
						<Box className="evidence-image-overlay-header-divider" />
						<h4>{`Time: ${moment(overlayDetails.timestamp).format("HH:mm:ss.SSS")}`}</h4>
					</Box>
				</Box>
			</Box>

			<Box sx={{ width: "100%", height: canvasWrapperHeight, position: "relative" }}>
				<canvas
					ref={canvasRef}
					width={canvasDimensions.width}
					height={canvasDimensions.height}
					style={{
						width: "100%",
						backgroundSize: "contain",
						backgroundPosition: "center",
						backgroundRepeat: "no-repeat",
						backgroundImage: `url(${src})`
					}}
				/>

				{violations.length > 0 ? (
					<Box className="evidence-image-violations-overlay">
						<Box className="evidence-image-violations-overlay-header">
							<h3>Violations</h3>
						</Box>

						<Box className="evidence-image-violations-list">
							{violations.map((violationItem, index) => (
								<h5 key={index}>
									{NON_HSRP_VIOLATION_NAMES.includes(violationItem.name) || violationItem.name === "Non HSRP"
										? "Non HSRP"
										: convertToTitleCase(violationItem.name.replace(/_/g, " "))}
									{violationItem.is_speed_related && overlayDetails.speed ? ` (${overlayDetails.speed})` : ""}
								</h5>
							))}
						</Box>
					</Box>
				) : null}

				{handleShowFullscreenImage ? (
					<IconButton
						disableRipple
						className="image-action-button right"
						onClick={() => handleShowFullscreenImage(src, overlayDetails, isFrame)}
					>
						<FullscreenIcon sx={{ color: "white" }} fontSize="large" />
					</IconButton>
				) : null}

				<IconButton
					disableRipple
					className="image-action-button left"
					onClick={handleDownload}
					disabled={downloadImageLoader}
				>
					{downloadImageLoader ? (
						<CircularProgress size={28} sx={{ color: "white" }} />
					) : (
						<DownloadIcon sx={{ color: "white" }} fontSize="large" />
					)}
				</IconButton>
			</Box>

			<Box className="evidence-image-overlay bottom">
				<Box className="evidence-image-overlay-content">
					<Box className="evidence-image-overlay-content-item">
						<h5>
							<span className="evidence-image-overlay-content-name">Plate Number: </span>
							{overlayDetails.numberPlate}
						</h5>
					</Box>

					<Box className="evidence-image-overlay-content-item">
						<h5>
							<span className="evidence-image-overlay-content-name">Officer: </span>
							{overlayDetails.officerName}
						</h5>
					</Box>

					<Box className="evidence-image-overlay-content-item">
						<h5>
							<span className="evidence-image-overlay-content-name">Location: </span>
							{overlayDetails.coordinates}
						</h5>
					</Box>

					{/* <Box className="evidence-image-overlay-content-item">
						<h5>
							<span className="evidence-image-overlay-content-name">Site: </span>
							{overlayDetails.site}
						</h5>
					</Box> */}
				</Box>
			</Box>

			<canvas
				ref={downloadCanvasRef}
				style={{
					width: "100%",
					backgroundSize: "contain",
					backgroundPosition: "center",
					backgroundRepeat: "no-repeat",
					backgroundImage: `url(${String(src)})`,
					position: "absolute",
					top: -99999,
					left: -99999,
					opacity: 0
				}}
			/>
		</Box>
	);
};

const EvidenceImage: FC<IEvidenceImageProps> = (props) => {
	const { bbox, vehicleLogDetails, violations, activeImage, onOpenFullscreenImage, onChangeActiveImage } = props;

	const componentWrapperRef = useRef<HTMLDivElement>(null);

	const [componentWidth, setComponentWidth] = useState<number>(0);

	// APIS
	// GET DEVICE CONFIG
	// const { data: siteConfig } = useGetDeviceConfigDetailsQuery(null, {
	// 	skip: !authState.user.token || authState.user.token === "invalid"
	// });

	// GET CLIENT DETAILS
	// const { data: clientDetails } = useGetClientDetailsQuery(null, {
	// 	skip: !siteConfig,
	// 	refetchOnMountOrArgChange: true
	// });

	const imageDimensions = useMemo<IDimensions>(() => {
		if (vehicleLogDetails) {
			return { width: vehicleLogDetails.image_width, height: vehicleLogDetails.image_height };
		}

		return { width: 1280, height: 720 };
	}, [vehicleLogDetails]);

	const imageUrls = useMemo<IEvidenceImageURLDetails[]>(() => {
		if (!vehicleLogDetails) return [];

		const frameImages: IEvidenceImageURLDetails[] = [];
		const evidenceImages: IEvidenceImageURLDetails[] = [];

		for (const item of vehicleLogDetails.vehicle_images) {
			if (item.evidence_type === "FRAME") frameImages.push(item);
			else evidenceImages.push(item);
		}

		return [...frameImages, ...evidenceImages];
	}, [vehicleLogDetails]);

	function handleShowFullscreenImage(src: string, overlayDetails: ICarouselOverlayDetails, isFrame: boolean) {
		if (onOpenFullscreenImage) {
			onOpenFullscreenImage({
				src: src,
				bbox: isFrame ? bbox : [],
				imageDimensions: imageDimensions
				// overlayDetails: overlayDetails
			});
		}
	}

	if (!vehicleLogDetails || vehicleLogDetails.vehicle_images.length <= 0) {
		return (
			<Box className="evidence-image-component-wrapper">
				<NoImageIcon width={58} fill="#d5d5d5" stroke="#d5d5d5" />
				<Typography variant="h6" color="#999999">
					Image not available
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			ref={componentWrapperRef}
			sx={{ flexGrow: 1 }}
			onLoad={(event) => setComponentWidth(event.currentTarget.offsetWidth ?? 0)}
		>
			<Carousel
				swipeable
				emulateTouch
				useKeyboardArrows
				showStatus={false}
				showIndicators={false}
				className="evidence-image-carousel"
				selectedItem={activeImage}
				onChange={onChangeActiveImage}
				// width={componentWidth}
				renderArrowNext={(clickHandler, hasNext) => (
					<Box className="carousel-arrow-wrapper next">
						<IconButton size="small" classes={{ root: "carousel-arrow" }} onClick={clickHandler} disabled={!hasNext}>
							<ArrowRightIcon sx={{ fontSize: "36px" }} />
						</IconButton>
					</Box>
				)}
				renderArrowPrev={(clickHandler, hasPrev) => (
					<Box className="carousel-arrow-wrapper prev">
						<IconButton size="small" classes={{ root: "carousel-arrow" }} onClick={clickHandler} disabled={!hasPrev}>
							<ArrowLeftIcon sx={{ fontSize: "36px" }} />
						</IconButton>
					</Box>
				)}
				renderThumbs={(children) =>
					children.map((item, index) => {
						const imageItem: IEvidenceImageURLDetails = JSON.parse(item as string);
						return <CarouselThumb src={String(imageItem.url)} key={index} />;
					})
				}
				renderItem={(item) => {
					const imageItem: IEvidenceImageURLDetails = JSON.parse(item as string);

					const overlayDetails: ICarouselOverlayDetails = {
						productName: "MTES",
						timestamp: vehicleLogDetails?.timestamp ?? "",
						numberPlate: vehicleLogDetails?.vehicle_number ?? "",
						violations: violations,
						speed: vehicleLogDetails?.radar_speed ?? vehicleLogDetails?.speed ?? "",
						coordinates: vehicleLogDetails?.location_coordinates ?? "",
						location: vehicleLogDetails?.location_name ?? "",
						officerName: vehicleLogDetails?.officer ?? ""
					};

					return (
						<CarouselItem
							src={String(imageItem.url)}
							frameID={vehicleLogDetails?.vehicle_image_frame_id ?? ""}
							imageDimensions={imageDimensions}
							overlayDetails={overlayDetails}
							handleShowFullscreenImage={handleShowFullscreenImage}
							bbox={bbox}
							violations={violations}
							key={String(imageItem.id)}
							width={componentWidth}
							isFrame={imageItem.evidence_type === "FRAME"}
						/>
					);
				}}
			>
				{imageUrls.map((imageItem) => JSON.stringify(imageItem))}
			</Carousel>
		</Box>
	);
};

export default EvidenceImage;
